import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Minus, Plus, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { LUX_EASE } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutModal({ open, onClose, product }) {
  const { t } = useLanguage();
  const c = t.checkout;
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", city: "", state: "", pincode: "" });
  const [units, setUnits] = useState(1);
  const [payment, setPayment] = useState("COD");
  const [placing, setPlacing] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
    if (!open) {
      setPlacing(false);
      setError("");
      setOrderId("");
      setUnits(1);
      setPayment("COD");
      setForm({ name: "", phone: "", email: "", address: "", city: "", state: "", pincode: "" });
    }
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const digits = form.phone.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, "");
    return (
      form.name.trim().length >= 2 &&
      /^\d{10}$/.test(digits) &&
      form.address.trim().length >= 8 &&
      form.city.trim().length >= 2 &&
      form.state.trim().length >= 2 &&
      /^\d{6}$/.test(form.pincode.replace(/\D/g, "")) &&
      units >= 1
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!validate()) {
      setError(c.error);
      return;
    }
    setPlacing(true);
    try {
      const res = await fetch(`${API}/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slug: product.slug,
          ...form,
          pincode: form.pincode.replace(/\D/g, ""),
          phone: form.phone.replace(/\D/g, ""),
          units,
          payment_method: payment,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.detail || "failed");
      setOrderId(data.order.order_id);
      setPlacing(false);
    } catch {
      setPlacing(false);
      setError(c.error);
    }
  };

  const inputCls =
    "w-full border hairline bg-white/[0.03] px-4 py-3 text-sm text-cream placeholder:text-cream/35 transition-colors focus:border-gold/60";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[80] overflow-y-auto bg-obsidian/85 p-4 backdrop-blur-sm sm:p-8"
          onClick={onClose}
          data-testid="checkout-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.45, ease: LUX_EASE }}
            className="relative mx-auto my-6 w-full max-w-2xl border hairline bg-velvet p-6 shadow-[0_0_80px_rgba(212,175,55,0.15)] sm:p-9"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <button
              onClick={onClose}
              data-testid="checkout-close-button"
              aria-label="Close"
              className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center border hairline text-cream/70 transition-colors hover:border-gold/50 hover:text-gold"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>

            {orderId ? (
              <div className="py-6 text-center" data-testid="checkout-success-panel">
                <CheckCircle2 className="mx-auto h-12 w-12 text-gold" strokeWidth={1.25} />
                <h3 className="mt-5 font-serif text-3xl text-cream">{c.successT}</h3>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-cream/65">{c.successB}</p>
                <p className="mt-6 text-[11px] uppercase tracking-luxe text-gold/90">{c.orderIdLabel}</p>
                <p className="mt-1 font-serif text-3xl tracking-[0.15em] text-gold" data-testid="checkout-order-id">
                  {orderId}
                </p>
                <div className="mt-8 flex flex-wrap justify-center gap-3">
                  <button
                    onClick={() => { onClose(); navigate(`/order/${orderId}`); }}
                    data-testid="checkout-view-order-button"
                    className="bg-gold px-7 py-3.5 text-sm font-medium uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:bg-gold-light"
                  >
                    {c.viewOrder}
                  </button>
                  <button
                    onClick={onClose}
                    data-testid="checkout-continue-button"
                    className="border border-gold/50 px-7 py-3.5 text-sm uppercase tracking-[0.15em] text-gold transition-all duration-300 hover:bg-gold/10"
                  >
                    {c.continue}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h3 className="font-serif text-3xl text-cream">{c.title}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-cream/60">{c.sub}</p>

                <div className="mt-6 flex items-center gap-4 border hairline bg-obsidian/40 p-4">
                  <img src={product.image} alt={product.name} className="h-16 w-16 border hairline object-cover" />
                  <div className="flex-1">
                    <p className="font-serif text-lg text-cream">{product.name}</p>
                    <p className="text-[12px] text-cream/50">{product.size} · {product.currency}{product.price}</p>
                  </div>
                  <div className="flex items-center border hairline">
                    <button type="button" onClick={() => setUnits((u) => Math.max(1, u - 1))} data-testid="checkout-qty-minus" className="flex h-10 w-10 items-center justify-center text-gold" aria-label="Decrease quantity">
                      <Minus className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                    <span className="w-8 text-center font-serif text-xl text-cream" data-testid="checkout-qty-value">{units}</span>
                    <button type="button" onClick={() => setUnits((u) => Math.min(20, u + 1))} data-testid="checkout-qty-plus" className="flex h-10 w-10 items-center justify-center text-gold" aria-label="Increase quantity">
                      <Plus className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>

                <form onSubmit={submit} noValidate data-testid="checkout-form">
                  <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    <input data-testid="checkout-name-input" className={inputCls} placeholder={c.namePh} value={form.name} onChange={set("name")} />
                    <input data-testid="checkout-phone-input" type="tel" inputMode="numeric" className={inputCls} placeholder={c.phonePh} value={form.phone} onChange={set("phone")} />
                    <input data-testid="checkout-email-input" type="email" className={`${inputCls} sm:col-span-2`} placeholder={`${c.email} ${c.emailOpt}`} value={form.email} onChange={set("email")} />
                    <input data-testid="checkout-address-input" className={`${inputCls} sm:col-span-2`} placeholder={c.addressPh} value={form.address} onChange={set("address")} />
                    <input data-testid="checkout-city-input" className={inputCls} placeholder={c.cityPh} value={form.city} onChange={set("city")} />
                    <input data-testid="checkout-state-input" className={inputCls} placeholder={c.statePh} value={form.state} onChange={set("state")} />
                    <input data-testid="checkout-pincode-input" inputMode="numeric" className={`${inputCls} sm:col-span-2`} placeholder={c.pincodePh} value={form.pincode} onChange={set("pincode")} />
                  </div>

                  <p className="mt-7 text-[11px] font-medium uppercase tracking-luxe text-gold/90">{c.payment}</p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {[
                      { id: "COD", label: c.cod, note: c.codNote, testid: "checkout-payment-cod" },
                      { id: "Prepaid", label: c.prepaid, note: c.prepaidNote, testid: "checkout-payment-prepaid" },
                    ].map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        data-testid={o.testid}
                        onClick={() => setPayment(o.id)}
                        className={`border p-4 text-left transition-all duration-300 ${
                          payment === o.id ? "border-gold bg-gold/10" : "hairline hover:border-gold/40"
                        }`}
                      >
                        <span className={`block font-serif text-lg ${payment === o.id ? "text-gold-light" : "text-cream"}`}>
                          {o.label}
                        </span>
                        <span className="mt-1 block text-[12px] leading-relaxed text-cream/55">{o.note}</span>
                      </button>
                    ))}
                  </div>

                  <div className="mt-7 border-t hairline pt-5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-cream/60">{c.total}</span>
                      <span className="font-serif text-3xl text-gold" data-testid="checkout-total-value">
                        {product.currency}{product.price * units}
                      </span>
                    </div>
                  </div>

                  {error && (
                    <p data-testid="checkout-error-message" className="mt-4 border border-gold/40 bg-gold/10 px-4 py-3 text-sm text-gold-light">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={placing}
                    data-testid="checkout-place-order-button"
                    className="mt-6 w-full bg-gold px-8 py-4 text-sm font-medium uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_36px_rgba(212,175,55,0.35)] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {placing ? c.placing : c.place}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
